import React, { useState } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SwitchToggle from "../../components/SwitchToggle";
import AddFeature from "../../Modals/addFeature";
import AddFeatureSubGroup from "../../Modals/addFeatureSubGroup";
import { SubGroup, Feature } from "../../types/types";

const FeatureGroupDetails: React.FC = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [openSubGroupModal, setOpenSubGroupModal] = useState(false);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [openRows, setOpenRows] = useState<number[]>([]);

  const toggleRow = (id: number) => {
    setOpenRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const [subGroups] = useState<SubGroup[]>([
    { id: 1, name: "Acoustic parking aid", groupId: Number(groupId), fieldType: "Checkbox", status: true },
    { id: 2, name: "Visual parking aid", groupId: Number(groupId), fieldType: "Checkbox", status: true },
  ]);

  const [features] = useState<Feature[]>([
    {
      id: 1,
      name: "Self-steering systems",
      groupId: Number(groupId),
      subGroupId: null,
      createdDate: "01-Sept-2025",
      status: true,
    },
    {
      id: 2,
      name: "In front",
      groupId: Number(groupId),
      subGroupId: 1,
      createdDate: "02-Sept-2025",
      status: true,
    },
    {
      id: 3,
      name: "Rear",
      groupId: Number(groupId),
      subGroupId: 1,
      createdDate: "02-Sept-2025",
      status: true,
    },
    {
      id: 4,
      name: "Camera",
      groupId: Number(groupId),
      subGroupId: 2,
      createdDate: "05-Sept-2025",
      status: true,
    },
    {
      id: 5,
      name: "360° camera",
      groupId: Number(groupId),
      subGroupId: 2,
      createdDate: "05-Sept-2025",
      status: true,
    },
  ]);

  const groupFeatures = features.filter((f) => f.subGroupId === null);

  return (
    <>
      <div className="main_title">
        <h1>
          <span className="c_primary">Parking assistance</span> : Feature Group Details
        </h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-features")}>
            Manage Features
          </button>{" "}
          - Group Detail
        </p>
      </div>

      {/* ================= Group Level Features ================= */}
      <div className="cards">
        <div className="table_header">
          <h3>Features in this Group</h3>
          <Button
            className="btn btn_primary"
            onClick={() => setOpenFeatureModal(true)}
          >
            <AddIcon /> Add Feature
          </Button>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feature Name</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Enable/Disable</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {groupFeatures.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <div className="user_block">
                      <p><b>{f.name}</b></p>
                    </div>
                  </TableCell>
                  <TableCell>{f.createdDate}</TableCell>
                  <TableCell>
                    <SwitchToggle defaultChecked={f.status} />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <IconButton onClick={() => setOpenFeatureModal(true)}>
                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                      </IconButton>
                      <IconButton>
                        <img src="/static/images/trash2_icon.svg" alt="Delete" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* ================= Sub Groups ================= */}
      <div className="cards mt_20">
        <div className="table_header">
          <h3>Sub Groups</h3>
          <Button
            className="btn btn_primary"
            onClick={() => setOpenSubGroupModal(true)}
          >
            <AddIcon /> Add Sub Group
          </Button>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sub Group Name</TableCell>
                <TableCell>Field Type</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Enable/Disable</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {subGroups.map((sg) => {
                const subGroupFeatures = features.filter(
                  (f) => f.subGroupId === sg.id
                );
                const isOpen = openRows.includes(sg.id);

                return (
                  <React.Fragment key={sg.id}>
                    <TableRow className={isOpen ? "row_open" : ""}>
                      <TableCell>
                        <div className="user_block click_action" onClick={() => toggleRow(sg.id)}>
                          <p><b>{sg.name}</b></p>
                        </div>
                      </TableCell>

                      <TableCell>{sg.fieldType}</TableCell>

                      <TableCell>01-Sept-2025</TableCell>

                      <TableCell>
                        <SwitchToggle defaultChecked={sg.status} />
                      </TableCell>

                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton onClick={() => toggleRow(sg.id)}>
                            {isOpen ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                          <IconButton onClick={() => setOpenSubGroupModal(true)}>
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                          <IconButton>
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Features */}
                    <TableRow>
                      <TableCell colSpan={5} style={{ padding: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit className="table_collapse">
                          <Box className="feature_grid gap_m">
                            {subGroupFeatures.map((f) => (
                              <div key={f.id} className="cards feature_card">
                                <h2>{f.name}</h2>
                                <p>{f.createdDate}</p>
                                <div className="table_actions v2">
                                  <SwitchToggle defaultChecked={f.status} />
                                  <IconButton onClick={() => setOpenFeatureModal(true)}>
                                    <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                  </IconButton>
                                  <IconButton>
                                    <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                  </IconButton>
                                </div>
                              </div>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <AddFeatureSubGroup
        open={openSubGroupModal}
        setOpen={setOpenSubGroupModal}
      />

      <AddFeature
        open={openFeatureModal}
        setOpen={setOpenFeatureModal}
        groupId={Number(groupId)}
        subGroups={subGroups}
      />
    </>
  );
};

export default FeatureGroupDetails;